import { useSyncExternalStore } from "react";
import { z } from "zod";

/**
 * Schema failures are the app's contract alarms: the API changed shape, or a
 * persisted blob is from an incompatible build. Both are recoverable — the app
 * falls back — but silently recovering hides a real defect, so each one raises a
 * toast that stays until it's dismissed by hand, and lands in the console with
 * the full zod report for anyone with devtools open.
 */

export interface SchemaError {
  id: string;
  /** What failed to parse, e.g. "AppState". Shown collapsed. */
  label: string;
  /** The full zod report. Shown when the toast is expanded. */
  detail: string;
  at: number;
}

let errors: SchemaError[] = [];
let seq = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

/** zod v4 ships a human-readable formatter; anything else gets stringified. */
function describe(error: unknown): string {
  if (error instanceof z.ZodError) {
    try {
      return z.prettifyError(error);
    } catch {
      return JSON.stringify(error.issues, null, 2);
    }
  }
  if (error instanceof Error) return error.stack ?? error.message;
  return String(error);
}

export function reportSchemaError(label: string, error: unknown): void {
  const detail = describe(error);
  // Console first, so the full object is inspectable even if the UI is broken.
  console.error(`[schema] failed to parse ${label}`, error);

  // The same failure re-raised — a retried fetch, or StrictMode invoking a state
  // initialiser twice — is one problem, not N. Stacking identical toasts would
  // just bury the map.
  if (errors.some((e) => e.label === label && e.detail === detail)) return;

  seq += 1;
  errors = [...errors, { id: `${label}-${seq}`, label, detail, at: Date.now() }];
  emit();
}

export function dismissSchemaError(id: string): void {
  errors = errors.filter((e) => e.id !== id);
  emit();
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

export function useSchemaErrors(): SchemaError[] {
  return useSyncExternalStore(
    subscribe,
    () => errors,
    () => errors,
  );
}
